<template>
  <div>
    <Modal
      v-if="shouldRenderModal"
      v-model="isOpen"
      :keep-mounted="true"
      title="求人へ応募するための必須項目を入力しましょう"
      :show-title="true"
      :show-close="false"
      :close-on-backdrop="true"
      :close-on-esc="false"
      :auto-focus="false"
      body-padding="0"
      footer-class="border-top"
      :max-width="960"
      @close="handleClose"
    >
      <div class="registration-modal__container">
        <!-- スクロール可能な本体 -->
        <div
          ref="modalBodyRef"
          class="registration-modal__body pt-0 px-0 pb-0 flex-grow-1 overflow-hidden d-flex flex-column transition-height"
          style="min-height: 0"
        >
          <!-- ステップタイトル -->
          <div class="pt-4 flex-shrink-0">
            <h3 class="text-center size-16 fw-bold mb-0">{{ stepTitle }}</h3>
            <div id="registration-step-message" class="mt-3 px-4 px-md-5"></div>
          </div>

          <!-- 入力エリア -->
          <div class="flex-grow-1 overflow-auto" style="min-height: 0">
            <transition
              :name="transitionName"
              mode="out-in"
              @before-leave="onBeforeLeave"
              @enter="onEnter"
              @after-enter="onAfterEnter"
            >
              <keep-alive>
                <component
                  :is="currentStepComponent"
                  class="h-100 d-flex flex-column"
                  style="min-height: 0"
                  @next="nextStep"
                  @back="prevStep"
                  @open-direct-apply="openDirectApplyModal"
                  @open-profile-guide="openProfileGuideModal"
                  @open-eeasy-schedule="openEeasyScheduleModal"
                  @open-platform-direct="openPlatformDirectModal"
                />
              </keep-alive>
            </transition>
          </div>
        </div>
      </div>
      <template #footer>
        <div id="registration-footer" class="w-100 flex-shrink-0 bg-white"></div>
      </template>
    </Modal>

    <DirectApplyModal
      v-if="showDirectApplyModal && directJob"
      v-model="showDirectApplyModal"
      :job="directJob"
      :is-applied="isDirectApplied"
      :is-applying="isDirectApplying"
      :on-submit="applyDirectJob"
    />
    <ProfileGuideModal
      v-if="showProfileGuideModal"
      v-model="showProfileGuideModal"
      :on-submit="openProfileGuide"
    />
    <RegistrationEeasyScheduleModal
      v-if="showEeasyScheduleModal"
      v-model="showEeasyScheduleModal"
      :on-submit="openEeasy"
    />
    <PlatformDirectModal v-model="showPlatformDirectModal" />
  </div>
</template>

<script setup lang="ts">
import { getJobDetail } from '@/apis/job.api';
import Modal from '@/components/shared/modal/Modal.vue';
import PlatformDirectModal from '@/components/shared/modal/PlatformDirectModal.vue';
import { EDIT_PROFILE_PATH } from '@/constant/routers/path';
import request from '@/services/http.service';
import { useRegistrationMasterDataStore } from '@/stores/registration/RegistrationMasterData.store';
import { useRegistrationStepsStore } from '@/stores/registration/RegistrationSteps.store';
import type { RecentlyViewedJob } from '@/types/models/recentlyViewedJob';
import { EEASY_URL } from '@/utils/constants';
import type { ApplyModalProps } from './types.d';
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
} from 'vue';

const props = defineProps<ApplyModalProps>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
}>();

const DIRECT_APPLY_STORAGE_KEY = 'direct_apply_job';
const Step1 = defineAsyncComponent(() => import('./Step1BasicInfo.vue'));
const Step2 = defineAsyncComponent(() => import('./Step2BasicInfo.vue'));
const Step3 = defineAsyncComponent(() => import('./Step3Career.vue'));
const Step4 = defineAsyncComponent(() => import('./Step4DesiredCondition.vue'));
const DirectApplyModal = defineAsyncComponent(() => import('./overlays/DirectApplyModal.vue'));
const ProfileGuideModal = defineAsyncComponent(() => import('./overlays/ProfileGuideModal.vue'));
const RegistrationEeasyScheduleModal = defineAsyncComponent(
  () => import('./overlays/RegistrationEeasyScheduleModal.vue')
);

const isOpen = ref(props.modelValue);
const isDismissed = ref(false);
const isClosing = ref(false);
const currentStep = ref(1);
const transitionName = ref('slide-fade');
const registrationMasterDataStore = useRegistrationMasterDataStore();
const registrationStepsStore = useRegistrationStepsStore();
const shouldRenderModal = ref(true);
const isInitialized = ref(false);
const isInitializing = ref(false);
const showDirectApplyModal = ref(false);
const showProfileGuideModal = ref(false);
const showEeasyScheduleModal = ref(false);
const showPlatformDirectModal = ref(false);
const directJob = ref<RecentlyViewedJob | null>(null);
const isDirectApplying = ref(false);
const isDirectApplied = ref(false);
watch(
  () => showEeasyScheduleModal.value,
  (value, prev) => {
    if (!value && prev) {
      handleClose();
    }
  }
);

watch(
  () => props.modelValue,
  (value, prev) => {
    if (value === isOpen.value) return;
    if (value && !prev) {
      isDismissed.value = false;
      isClosing.value = false;
      void initializeModal(true, props.shouldShowStepsRegistration);
    }
    isOpen.value = value;
  }
);

watch(
  () => isOpen.value,
  (value, prev) => {
    if (!value && prev) {
      isDismissed.value = true;
    }
    if (value !== props.modelValue) {
      emit('update:modelValue', value);
    }
  }
);

const handleClose = async (): Promise<void> => {
  if (isClosing.value) return;
  isClosing.value = true;
  await nextTick();
  isOpen.value = false;
  await nextTick();
  isClosing.value = false;
};

const footerVisible = computed(() => isOpen.value && !isClosing.value);
provide('registrationFooterVisible', footerVisible);

const stepTitle = computed(() => {
  switch (currentStep.value) {
    case 1:
      return '基本情報(1/2)';
    case 2:
      return '基本情報(2/2)';
    case 3:
      return 'ご経験について';
    case 4:
      return '希望条件';
    default:
      return '';
  }
});

const currentStepComponent = computed(() => {
  switch (currentStep.value) {
    case 1:
      return Step1;
    case 2:
      return Step2;
    case 3:
      return Step3;
    case 4:
      return Step4;
    default:
      return Step1;
  }
});

const initializeModal = async (
  forceOpen: boolean,
  shouldShowFromDashboard?: boolean
): Promise<void> => {
  if (isInitialized.value || isInitializing.value) {
    if (forceOpen) {
      isDismissed.value = false;
      isOpen.value = true;
    }
    return;
  }
  isInitializing.value = true;
  try {
    const options =
      shouldShowFromDashboard !== undefined
        ? { shouldShowStepsRegistration: shouldShowFromDashboard }
        : undefined;
    const force =
      shouldShowFromDashboard === true && registrationStepsStore.step4Completed === true;
    await registrationStepsStore.initialize(force, options);
    if (!forceOpen && !registrationStepsStore.shouldDisplayModal) {
      shouldRenderModal.value = false;
      return;
    }
    await registrationMasterDataStore.loadStep1Data();
    await registrationMasterDataStore.loadStep2Data();
    isInitialized.value = true;

    if (!isDismissed.value && (forceOpen || registrationStepsStore.shouldDisplayModal)) {
      isOpen.value = true;
    }
  } catch (error) {
    console.error('[RegistrationModal] initialization failed', error);
    shouldRenderModal.value = false;
  } finally {
    isInitializing.value = false;
  }
};

watch(
  () => props.shouldShowStepsRegistration,
  (val) => {
    if (isInitialized.value || isInitializing.value) return;
    if (val === undefined) return;
    void initializeModal(false, val);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  isOpen.value = false;
});

const nextStep = async () => {
  if (currentStep.value < 4) {
    transitionName.value = 'slide-fade';
    currentStep.value++;
    return;
  }
  await handleRegistrationCompleted();
};

const prevStep = () => {
  if (currentStep.value > 1) {
    transitionName.value = 'slide-fade-reverse';
    currentStep.value--;
  }
};

watch(
  () => currentStep.value,
  (step) => {
    if (step === 2) {
      registrationMasterDataStore.loadStep3Data();
    }
  },
  { immediate: true }
);

defineExpose({
  open: async () => {
    isDismissed.value = false;
    await initializeModal(true, props.shouldShowStepsRegistration);
    if (shouldRenderModal.value) {
      isOpen.value = true;
    }
  },
});

const modalBodyRef = ref<HTMLElement | null>(null);

const onBeforeLeave = () => {
  if (modalBodyRef.value) {
    modalBodyRef.value.style.height = `${modalBodyRef.value.offsetHeight}px`;
  }
};

const onEnter = () => {
  if (modalBodyRef.value) {
    const oldHeight = modalBodyRef.value.style.height;

    // 新しい高さを計測
    modalBodyRef.value.style.height = 'auto';
    const newHeight = modalBodyRef.value.offsetHeight;

    // アニメーション開始のために旧高さへ戻す
    modalBodyRef.value.style.height = oldHeight;

    // 再描画を強制
    modalBodyRef.value.offsetHeight;

    // 計測した高さへアニメーション
    requestAnimationFrame(() => {
      if (modalBodyRef.value) {
        modalBodyRef.value.style.height = `${newHeight}px`;
      }
    });
  }
};

const onAfterEnter = () => {
  if (modalBodyRef.value) {
    modalBodyRef.value.style.height = '';
  }
};

const getDirectApplyJobFromStorage = (): RecentlyViewedJob | null => {
  try {
    const stored = localStorage.getItem(DIRECT_APPLY_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.jobId) {
      localStorage.removeItem(DIRECT_APPLY_STORAGE_KEY);
      return null;
    }
    return {
      platform: 'direct',
      jobId: parsed.jobId,
      title: parsed.jobTitle ?? parsed.title,
      companyName: parsed.companyName,
    };
  } catch (error) {
    console.error('[RegistrationModal] failed to parse direct apply context', error);
    localStorage.removeItem(DIRECT_APPLY_STORAGE_KEY);
    return null;
  }
};

const clearDirectApplyJobStorage = (): void => {
  try {
    localStorage.removeItem(DIRECT_APPLY_STORAGE_KEY);
  } catch {
    console.error('[RegistrationModal] failed to clear direct apply job storage');
  }
};

const handleRegistrationCompleted = async (): Promise<void> => {
  const directApplyJob = getDirectApplyJobFromStorage();
  handleClose();
  if (directApplyJob) {
    clearDirectApplyJobStorage();
    await openDirectApplyModal(directApplyJob);
    return;
  }
  openProfileGuideModal();
};

const fetchJobSummary = async (
  jobId?: string | number | null
): Promise<Partial<RecentlyViewedJob>> => {
  if (jobId === undefined || jobId === null) return {};
  try {
    const res = await getJobDetail(String(jobId));
    return {
      companyName: res?.data?.company_name ?? undefined,
      title: res?.data?.title ?? undefined,
    };
  } catch (error) {
    console.error('[RegistrationModal] failed to fetch job summary', error);
    return {};
  }
};

const openDirectApplyModal = async (job: RecentlyViewedJob): Promise<void> => {
  const shouldFetchDetail = !job.companyName || !job.title;
  const jobSummary = shouldFetchDetail ? await fetchJobSummary(job.jobId) : {};
  directJob.value = { ...job, ...jobSummary };
  isDirectApplying.value = false;
  isDirectApplied.value = false;
  showDirectApplyModal.value = true;
  isOpen.value = false;
};

const applyDirectJob = async (): Promise<void> => {
  if (!directJob.value?.jobId) {
    return;
  }
  try {
    isDirectApplying.value = true;
    const formData = new FormData();
    formData.append('propositions_job_applications_create_form[apply_message]', '');
    const res = await request(`/propositions/job_applications?job_id=${directJob.value.jobId}`, {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (res && (res.status === 200 || res.data?.status === 200)) {
      isDirectApplied.value = true;
      return;
    }
  } catch (error) {
    console.error('[RegistrationModal] apply job failed', error);
  } finally {
    isDirectApplying.value = false;
  }
};

const openProfileGuideModal = (): void => {
  showProfileGuideModal.value = true;
  isOpen.value = false;
};

const openProfileGuide = (): void => {
  window.location.href = EDIT_PROFILE_PATH;
};

const openEeasyScheduleModal = (): void => {
  showEeasyScheduleModal.value = true;
  isOpen.value = false;
};

const closeEeasyScheduleModal = (): void => {
  showEeasyScheduleModal.value = false;
  handleClose();
};

const openEeasy = (): void => {
  window.open(EEASY_URL, '_blank');
  closeEeasyScheduleModal();
};

const openPlatformDirectModal = (): void => {
  showPlatformDirectModal.value = true;
  isOpen.value = false;
};
</script>

<style lang="scss" scoped>
.transition-height {
  transition: height 0.5s ease-in-out;
}

.slide-fade-enter-active,
.slide-fade-leave-active,
.slide-fade-reverse-enter-active,
.slide-fade-reverse-leave-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-fade-reverse-enter-from {
  transform: translateX(-20px);
  opacity: 0;
}

.slide-fade-reverse-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.registration-modal__container {
  height: calc(90vh - 92px - 72px);
  max-height: calc(90vh - 92px - 72px);
  display: flex;
  flex-direction: column;
  overflow: hidden; // アニメーションのはみ出し防止
}

.registration-modal__body {
  overflow-x: hidden;
}
</style>
